import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Badge } from "@/components/ui/badge";
import { 
  CalendarIcon, MapPin, Users, Stethoscope, Filter, X, Check, ChevronsUpDown, RotateCcw, Search, Zap
} from "lucide-react";
import { cn } from "@/lib/utils";
import { API } from "@/config/api";
import { AREAS, REP_DIRECTORY, getRepDisplayName } from "@/config/constants";
import LocationSelector from "@/components/LocationSelector";

export const TERRITORIES = AREAS; // Re-export for backward compatibility

/**
 * Get default filter values (current month)
 */
export const getDefaultFilters = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const lastDay = new Date(year, now.getMonth() + 1, 0).getDate();
  
  return {
    startDate: `${year}-${month}-01`,
    endDate: `${year}-${month}-${lastDay}`,
    territory: "",
    subregion: "",
    suburb: "",
    rep: "",
    doctorId: "",
    selectedDoctor: null
  };
};

/**
 * Get "This Week" date range (Monday to Sunday of current week)
 */
export const getThisWeekFilters = () => {
  const now = new Date();
  const dayOfWeek = now.getDay();
  const diffToMonday = dayOfWeek === 0 ? -6 : 1 - dayOfWeek; // Sunday is 0
  
  const monday = new Date(now);
  monday.setDate(now.getDate() + diffToMonday);
  
  const sunday = new Date(monday);
  sunday.setDate(monday.getDate() + 6);
  
  const formatDate = (d) => {
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };
  
  return {
    startDate: formatDate(monday),
    endDate: formatDate(sunday),
    territory: "",
    subregion: "",
    suburb: "",
    rep: "",
    doctorId: "",
    selectedDoctor: null
  };
};

/**
 * Get "Last Month" date range
 */
export const getLastMonthFilters = () => {
  const now = new Date();
  const lastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  const lastDayOfLastMonth = new Date(now.getFullYear(), now.getMonth(), 0).getDate();
  
  const year = lastMonth.getFullYear();
  const month = String(lastMonth.getMonth() + 1).padStart(2, '0');
  
  return {
    startDate: `${year}-${month}-01`,
    endDate: `${year}-${month}-${String(lastDayOfLastMonth).padStart(2, '0')}`,
    territory: "",
    subregion: "",
    suburb: "",
    rep: "",
    doctorId: "",
    selectedDoctor: null
  };
};

/**
 * Controlled FilterBar component - does NOT manage its own state
 * Parent must pass filter values and onChange handlers
 * 
 * @param {Object} props
 * @param {Object} props.filters - Current filter values { startDate, endDate, territory, rep, doctorId, selectedDoctor }
 * @param {Function} props.onFilterChange - Called when any filter input changes: (key, value) => void
 * @param {Function} props.onApply - Called when Apply button clicked
 * @param {Function} props.onClear - Called when Clear button clicked
 * @param {Function} props.onPresetApply - Called when a preset is applied with new filters: (filters) => void
 * @param {boolean} props.showDoctorFilter
 * @param {boolean} props.showRepFilter
 * @param {boolean} props.showTerritoryFilter
 * @param {boolean} props.showDateFilter
 * @param {string} props.className
 */
const FilterBar = ({ 
  filters,
  onFilterChange,
  onApply,
  onClear,
  onPresetApply,
  showDoctorFilter = true,
  showRepFilter = true,
  showTerritoryFilter = true,
  showDateFilter = true,
  className = ""
}) => {
  // Doctor search state (local only - doesn't affect parent)
  const [doctorSearchOpen, setDoctorSearchOpen] = useState(false);
  const [doctorSearchQuery, setDoctorSearchQuery] = useState("");
  const [doctorSearchResults, setDoctorSearchResults] = useState([]);
  const [searchingDoctors, setSearchingDoctors] = useState(false);
  const [activePreset, setActivePreset] = useState(null);

  // Apply preset and trigger data fetch
  const applyPreset = useCallback((presetName, presetFilters) => {
    setActivePreset(presetName);
    if (onPresetApply) {
      onPresetApply(presetFilters);
    }
  }, [onPresetApply]);

  // Handle "This Week" preset
  const handleThisWeek = useCallback(() => {
    const weekFilters = getThisWeekFilters();
    applyPreset("thisWeek", weekFilters);
  }, [applyPreset]);

  // Handle "Last Month" preset
  const handleLastMonth = useCallback(() => {
    const monthFilters = getLastMonthFilters();
    applyPreset("lastMonth", monthFilters);
  }, [applyPreset]);

  // Handle territory quick-select
  const handleTerritoryPreset = useCallback((territory) => {
    const defaults = getDefaultFilters();
    const territoryFilters = { ...defaults, territory };
    applyPreset(`territory-${territory}`, territoryFilters);
  }, [applyPreset]);

  // Search doctors with debounce
  const searchDoctors = useCallback(async (query) => {
    try {
      setSearchingDoctors(true);
      const url = query.length >= 2 
        ? `${API}/doctors/search?q=${encodeURIComponent(query)}&limit=15`
        : `${API}/doctors/search?limit=10`;
      const response = await axios.get(url);
      setDoctorSearchResults(response.data);
    } catch (error) {
      console.error("Failed to search doctors");
    } finally {
      setSearchingDoctors(false);
    }
  }, []);

  // Debounced search effect
  useEffect(() => {
    if (!doctorSearchOpen) return;
    
    const timer = setTimeout(() => {
      searchDoctors(doctorSearchQuery);
    }, 300);
    return () => clearTimeout(timer);
  }, [doctorSearchQuery, doctorSearchOpen, searchDoctors]);

  // Handle doctor selection
  const handleDoctorSelect = (doctor) => {
    onFilterChange("doctorId", doctor.doctor_id);
    onFilterChange("selectedDoctor", doctor);
    setDoctorSearchOpen(false);
    setDoctorSearchQuery("");
  };

  // Clear doctor selection
  const clearDoctor = (e) => {
    e?.stopPropagation();
    onFilterChange("doctorId", "");
    onFilterChange("selectedDoctor", null);
  };

  // Count active filters
  const defaults = getDefaultFilters();
  const activeFilterCount = [
    filters.territory && filters.territory !== "all" ? filters.territory : "",
    filters.rep && filters.rep !== "all" ? filters.rep : "",
    filters.doctorId,
    (filters.startDate !== defaults.startDate || filters.endDate !== defaults.endDate) ? "date" : ""
  ].filter(Boolean).length;

  const hasActiveFilters = activeFilterCount > 0;

  // Clear active preset when filters change manually
  const handleClearWithPreset = useCallback(() => {
    setActivePreset(null);
    onClear();
  }, [onClear]);

  return (
    <Card className={cn("border-dashed", className)} data-testid="filter-bar">
      <CardContent className="p-3 sm:p-4">
        {/* Quick Presets */}
        <div className="flex flex-wrap items-center gap-2 mb-3 pb-3 border-b">
          <div className="flex items-center gap-1.5 mr-2">
            <Zap className="w-3.5 h-3.5 text-amber-500" />
            <span className="text-xs font-medium text-muted-foreground">Quick Filters:</span>
          </div>
          <Button
            variant={activePreset === "thisWeek" ? "default" : "outline"}
            size="sm"
            onClick={handleThisWeek}
            className="h-7 px-2.5 text-xs gap-1"
            data-testid="preset-this-week"
          >
            <CalendarIcon className="w-3 h-3" />
            This Week
          </Button>
          <Button
            variant={activePreset === "lastMonth" ? "default" : "outline"}
            size="sm"
            onClick={handleLastMonth}
            className="h-7 px-2.5 text-xs gap-1"
            data-testid="preset-last-month"
          >
            <CalendarIcon className="w-3 h-3" />
            Last Month
          </Button>
          <div className="h-4 w-px bg-border mx-1 hidden sm:block" />
          {TERRITORIES.map((t) => (
            <Button
              key={t}
              variant={activePreset === `territory-${t}` ? "default" : "outline"}
              size="sm"
              onClick={() => handleTerritoryPreset(t)}
              className="h-7 px-2.5 text-xs gap-1"
              data-testid={`preset-territory-${t.toLowerCase()}`}
            >
              <MapPin className="w-3 h-3" />
              {t}
            </Button>
          ))}
        </div>

        <div className="flex items-center gap-2 mb-3">
          <Filter className="w-4 h-4 text-muted-foreground" />
          <span className="text-sm font-medium text-muted-foreground">Custom Filters</span>
          {activeFilterCount > 0 && (
            <Badge variant="secondary" className="text-xs h-5 px-1.5">
              {activeFilterCount} active
            </Badge>
          )}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-5 gap-3">
          {/* Date Range Filter */}
          {showDateFilter && (
            <div className="sm:col-span-2 lg:col-span-2 xl:col-span-2">
              <Label className="text-xs text-muted-foreground mb-1 block">Date Range</Label>
              <div className="flex items-center gap-2">
                <div className="relative flex-1">
                  <CalendarIcon className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
                  <Input
                    type="date"
                    value={filters.startDate || ""}
                    onChange={(e) => onFilterChange("startDate", e.target.value)}
                    className="h-9 pl-8 text-xs"
                    data-testid="filter-start-date"
                  />
                </div>
                <span className="text-muted-foreground text-xs">to</span>
                <div className="relative flex-1">
                  <CalendarIcon className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
                  <Input
                    type="date"
                    value={filters.endDate || ""}
                    onChange={(e) => onFilterChange("endDate", e.target.value)}
                    className="h-9 pl-8 text-xs"
                    data-testid="filter-end-date"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Area / Location Filter */}
          {showTerritoryFilter && (
            <div className="sm:col-span-2 lg:col-span-1">
              <LocationSelector
                area={filters.territory || ""}
                subregion={filters.subregion || ""}
                suburb={filters.suburb || ""}
                onAreaChange={(v) => onFilterChange("territory", v)}
                onSubregionChange={(v) => onFilterChange("subregion", v)}
                onSuburbChange={(v) => onFilterChange("suburb", v)}
                compact
              />
            </div>
          )}

          {/* Rep Filter */}
          {showRepFilter && (
            <div>
              <Label className="text-xs text-muted-foreground mb-1 block">Rep</Label>
              <Select 
                value={filters.rep || "all"} 
                onValueChange={(val) => onFilterChange("rep", val === "all" ? "" : val)}
              >
                <SelectTrigger className="h-9 text-xs" data-testid="filter-rep">
                  <Users className="w-3.5 h-3.5 mr-1.5 text-muted-foreground" />
                  <SelectValue placeholder="All" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Reps</SelectItem>
                  {REP_DIRECTORY.map((name) => (
                    <SelectItem key={name} value={name}>{name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Doctor Filter */}
          {showDoctorFilter && (
            <div className="sm:col-span-2 lg:col-span-1">
              <Label className="text-xs text-muted-foreground mb-1 block">Doctor</Label>
              <Popover open={doctorSearchOpen} onOpenChange={setDoctorSearchOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    role="combobox"
                    aria-expanded={doctorSearchOpen}
                    className={cn(
                      "w-full justify-between h-9 text-xs font-normal",
                      !filters.selectedDoctor && "text-muted-foreground"
                    )}
                    data-testid="filter-doctor"
                  >
                    <div className="flex items-center gap-1.5 truncate">
                      <Stethoscope className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" />
                      <span className="truncate">
                        {filters.selectedDoctor ? filters.selectedDoctor.doctor_name : "All Doctors"}
                      </span>
                    </div>
                    {filters.selectedDoctor ? (
                      <X 
                        className="w-3.5 h-3.5 text-muted-foreground hover:text-foreground flex-shrink-0" 
                        onClick={clearDoctor}
                      />
                    ) : (
                      <ChevronsUpDown className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" />
                    )}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[280px] p-0" align="start">
                  <Command shouldFilter={false}>
                    <CommandInput
                      placeholder="Search doctors..."
                      value={doctorSearchQuery}
                      onValueChange={setDoctorSearchQuery}
                      className="h-9"
                    />
                    <CommandList className="max-h-[200px]">
                      {searchingDoctors ? (
                        <div className="py-4 text-center text-xs text-muted-foreground">
                          <div className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-1"></div>
                          Searching...
                        </div>
                      ) : doctorSearchResults.length === 0 ? (
                        <CommandEmpty className="py-4 text-xs">No doctors found</CommandEmpty>
                      ) : (
                        <CommandGroup>
                          {doctorSearchResults.map((doctor) => (
                            <CommandItem
                              key={doctor.doctor_id}
                              value={doctor.doctor_id}
                              onSelect={() => handleDoctorSelect(doctor)}
                              className="cursor-pointer py-2"
                            >
                              <Check
                                className={cn(
                                  "mr-2 h-3.5 w-3.5",
                                  filters.doctorId === doctor.doctor_id ? "opacity-100" : "opacity-0"
                                )}
                              />
                              <div className="flex flex-col">
                                <span className="text-xs font-medium">{doctor.doctor_name}</span>
                                <span className="text-[10px] text-muted-foreground">
                                  {doctor.practice_name || doctor.city_territory}
                                </span>
                              </div>
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      )}
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            </div>
          )}
        </div>

        {/* Action Buttons */}
        <div className="flex flex-wrap items-center gap-2 mt-4 pt-3 border-t">
          <Button
            onClick={() => { setActivePreset(null); onApply(); }}
            size="sm"
            className="h-8 px-4 gap-1.5"
            data-testid="apply-filters-button"
          >
            <Search className="w-3.5 h-3.5" />
            Apply Filters
          </Button>
          
          <Button
            onClick={handleClearWithPreset}
            variant="outline"
            size="sm"
            className="h-8 px-3 gap-1.5"
            data-testid="clear-filters-button"
          >
            <RotateCcw className="w-3.5 h-3.5" />
            Clear
          </Button>

          {/* Active Filter Badges */}
          {hasActiveFilters && (
            <div className="flex flex-wrap gap-1.5 ml-2">
              {filters.territory && filters.territory !== "all" && (
                <Badge variant="secondary" className="text-xs h-6 gap-1 pr-1">
                  <MapPin className="w-3 h-3" />
                  {filters.territory}
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-4 w-4 p-0 ml-1 hover:bg-transparent"
                    onClick={() => onFilterChange("territory", "")}
                  >
                    <X className="w-3 h-3" />
                  </Button>
                </Badge>
              )}
              {filters.rep && filters.rep !== "all" && (
                <Badge variant="secondary" className="text-xs h-6 gap-1 pr-1">
                  <Users className="w-3 h-3" />
                  Rep {filters.rep}
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-4 w-4 p-0 ml-1 hover:bg-transparent"
                    onClick={() => onFilterChange("rep", "")}
                  >
                    <X className="w-3 h-3" />
                  </Button>
                </Badge>
              )}
              {filters.selectedDoctor && (
                <Badge variant="secondary" className="text-xs h-6 gap-1 pr-1">
                  <Stethoscope className="w-3 h-3" />
                  {filters.selectedDoctor.doctor_name}
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-4 w-4 p-0 ml-1 hover:bg-transparent"
                    onClick={clearDoctor}
                  >
                    <X className="w-3 h-3" />
                  </Button>
                </Badge>
              )}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default FilterBar;
